import type {
  CompetitorLine,
  IdeaStatus,
  MarketIdea,
  MarketPriority,
  OpportunityStage,
  OpportunityType,
  TrackingStatus,
} from "./types";

/** Display order: most urgent first. */
export const PRIORITIES: readonly MarketPriority[] = ["alta", "media", "baja"];

/** Board order (left to right). */
export const IDEA_STATUSES: readonly IdeaStatus[] = [
  "nueva",
  "evaluando",
  "en_marcha",
  "descartada",
];

export const COMPETITOR_LINES: readonly CompetitorLine[] = [
  "avante_crm_clinicas",
  "agente_licitaciones",
  "automatizacion_pymes",
  "otro",
];

/** Menu order: the default first, then by how much attention it needs. */
export const TRACKING_STATUSES: readonly TrackingStatus[] = [
  "vigilar",
  "amenaza_alta",
  "analizado",
  "descartado",
];

export const OPPORTUNITY_TYPES: readonly OpportunityType[] = [
  "cliente_potencial",
  "licitacion",
  "partner",
  "idea",
];

/** Kanban order (left to right). */
export const OPPORTUNITY_STAGES: readonly OpportunityStage[] = [
  "nueva",
  "en_contacto",
  "propuesta",
  "ganada",
  "perdida",
];

/** Only these types can become a deal. */
export const CONVERTIBLE_TYPES: readonly OpportunityType[] = [
  "cliente_potencial",
  "licitacion",
];

export const priorityLabelKey = (priority: MarketPriority): string =>
  `market.priority.${priority}`;

export const ideaStatusLabelKey = (status: IdeaStatus): string =>
  `market.idea_status.${status}`;

export const lineLabelKey = (line: CompetitorLine): string =>
  `market.lines.${line}`;

export const trackingStatusLabelKey = (status: TrackingStatus): string =>
  `market.tracking_status.${status}`;

export const opportunityTypeLabelKey = (type: OpportunityType): string =>
  `market.opportunity_type.${type}`;

export const opportunityStageLabelKey = (stage: OpportunityStage): string =>
  `market.opportunity_stage.${stage}`;

export const isTrackingStatus = (value: unknown): value is TrackingStatus =>
  TRACKING_STATUSES.includes(value as TrackingStatus);

export const isOpportunityType = (value: unknown): value is OpportunityType =>
  OPPORTUNITY_TYPES.includes(value as OpportunityType);

export const isOpportunityStage = (value: unknown): value is OpportunityStage =>
  OPPORTUNITY_STAGES.includes(value as OpportunityStage);

export const isPriority = (value: unknown): value is MarketPriority =>
  PRIORITIES.includes(value as MarketPriority);

export const isIdeaStatus = (value: unknown): value is IdeaStatus =>
  IDEA_STATUSES.includes(value as IdeaStatus);

export const isCompetitorLine = (value: unknown): value is CompetitorLine =>
  COMPETITOR_LINES.includes(value as CompetitorLine);

/** Unknown values (bad data inserted by hand) sort last. */
const rank = <T>(list: readonly T[], value: T) => {
  const index = list.indexOf(value);
  return index === -1 ? list.length : index;
};

export const comparePriority = (
  left: MarketPriority,
  right: MarketPriority,
): number => rank(PRIORITIES, left) - rank(PRIORITIES, right);

/** Highest priority first, then the most recently touched. */
export const compareIdeas = (left: MarketIdea, right: MarketIdea): number =>
  comparePriority(left.priority, right.priority) ||
  (right.updated_at ?? "").localeCompare(left.updated_at ?? "") ||
  Number(right.id) - Number(left.id);
